//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useContext, useEffect, useState } from "react";
import { Card, Container, Table, Pagination, Row, Col, Form } from "react-bootstrap";
import { useTable, useSortBy, usePagination } from "react-table";
import { Link } from 'react-router-dom';
import { Context } from "../common/context/Context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSort,
    faSortUp,
    faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { CandidateSearchActions } from "./candidateSearchActions";
import ErrorAlertMessage from "../common/alertMessages/errorAlertMessage";
import { commonMessages } from "../JSON/commonMessages";

const SearchResultsPagination = ({ columns, data, tableJson, candidateCMSData }) => {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0 },
        },
        useSortBy,
        usePagination
    );
    const [commonComponentCMSData, setCommonComponentCMSData] = useState({});

    const fetchCommonComponentLabel = (key) => {
        if (commonComponentCMSData && commonComponentCMSData.Labels) {
            return commonComponentCMSData.Labels.find((el) => el.Key === key)?.Phrase || '';
        }
        return '';
    }

    const returnPageIndexText = (currentPage, TotalPage) => {
        const pageText = fetchCommonComponentLabel("Common.Page");
        let replaceText = pageText.replace('Page', '<span>Page</span>').replace('#Currentpage#', '<strong>' + currentPage + '</strong>').replace('#Totalpage#', '<strong>' + TotalPage + '</strong>');
        return (
            <div className="me-2 mt-1" dangerouslySetInnerHTML={{ __html: replaceText }} />
        )
    }

    useEffect(() => {
        setCommonComponentCMSData(candidateCMSData?.pageContent["CommonComponent"]);
    }, [candidateCMSData]);

    return (
        <Card>
            <Card.Header>
                <Card.Title className="mb-0">{tableJson.searchResultTitle}</Card.Title>
            </Card.Header>
            <Card.Body>
                <h6 className="text-muted">{tableJson.noOfResultsDesc} {rows.length}</h6>
                <Table className="mb-0" {...getTableProps()} responsive>
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                        {column.render("Header")}
                                        <span>
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                                                ) : (
                                                    <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                                                )
                                            ) : (
                                                <FontAwesomeIcon icon={faSort} className="ms-2" />
                                            )}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row, i) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => {
                                        return (
                                            <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>

                <Row className="mt-3">

                    <Col md="6" className="d-flex flex-lg-row flex-column">
                        {returnPageIndexText(pageIndex + 1, pageOptions.length)}
                        <div className="me-2">
                            <span className="me-2">{fetchCommonComponentLabel('Common.Show')}</span>
                            <Form.Select
                                className="d-inline-block w-auto"
                                value={pageSize}
                                aria-label="pageNumber"
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                }}
                            >
                                {[10, 20, 30, 40, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                        <div className="me-2">
                            <span className="me-2">{fetchCommonComponentLabel('Common.GoToPage')}</span>
                            <Form.Control
                                className="d-inline-block"
                                type="number"
                                aria-label="number"
                                min="1"
                                defaultValue={pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    gotoPage(page);
                                }}
                                style={{ width: "75px" }}
                            />
                        </div>
                    </Col>
                    <Col md="6" className="mt-1">
                        <Pagination className="float-lg-end float-md-end float-left">
                            <Pagination.First
                                onClick={() => gotoPage(0)}
                                disabled={!canPreviousPage}
                            />
                            <Pagination.Prev
                                onClick={() => previousPage()}
                                disabled={!canPreviousPage}
                            />
                            <Pagination.Next
                                onClick={() => nextPage()}
                                disabled={!canNextPage}
                            />
                            <Pagination.Last
                                onClick={() => gotoPage(pageCount - 1)}
                                disabled={!canNextPage}
                            />
                        </Pagination>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

const CandidateSearchResults = ({ candidateCMSData, searchData }) => {
    const [searchResultsJson, setSearchResultsJson] = useState({});
    const [candidateSearchCMSData, setCandidateSearchCMSData] = useState({});
    const context = useContext(Context);

    const [errorAlert, setErrorAlert] = useState({
        showAlert: false
    })

    // const fetchSearchResultsJson = async () => {

    //     let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    //     await CandidateSearchActions.GetSearchResultsJSON().then(
    //         (res) => {
    //             if (res.status === 200 || res.status === 204) {
    //                 setSearchResultsJson(JSON.parse(JSON.stringify(res?.data)));
    //             }
    //             else {
    //                 refErrorMessageAlert.showAlert = true;
    //                 setErrorAlert(refErrorMessageAlert);
    //                 window.scrollTo(0, 0);
    //             }
    //         },
    //         (err) => {
    //             console.log(err);
    //         }
    //     );
    // }

    const fetchSearchResultLabel = (key) => {
        if (candidateSearchCMSData && candidateSearchCMSData.Labels) {
            return candidateSearchCMSData.Labels.find((el) => el.Key === key)?.Phrase || '';
        }
        return '';
    }

    const trackAction = async (e, candidateId, firstName, lastName, referenceCode) => {

        let todayDate = new Date();
        let userId = context.userAccountData && context.userAccountData?.id;
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

        let payload = {
            "StaffId": userId,
            "SourceId": candidateId + "," + referenceCode,
            "Type": "Candidate",
            "Value": firstName + " " + lastName,
            "TriggeredTimeStamp": todayDate,
            "CreatedTimeStamp": todayDate,
            "CreatedBy": userId,
            "UpdatedTimeStamp": todayDate,
            "UpdatedBy": "NULL"
        }

        await CandidateSearchActions.TrackAction(payload).then(
            (res) => {
                if (res.status === 200 || res.status === 204) {
                    console.log("api succeeded")
                }
                else {
                    refErrorMessageAlert.showAlert = true;
                    setErrorAlert(refErrorMessageAlert);
                    window.scrollTo(0, 0);
                }
            },
            (err) => {
                console.log(err);
            }
        );
    }

    const searchTableColumns = [
        {
            id: 'CandidateSearchResults.Column.CandidateName',
            Header: fetchSearchResultLabel('CandidateSearchResults.Column.CandidateName'),
            accessor: (values) => {
                const { firstName, lastName } = values;
                let name = "";
                if (firstName && lastName) {
                    name = lastName + ',' + firstName;
                } else if (firstName && !lastName) {
                    name = firstName
                } else if (!firstName && lastName) {
                    name = lastName
                }
                return name;
            },
            Cell: ({ row, value }) =>
            (<Link to={'/candidateProfile'} onClick={(e) => trackAction(e, row?.original?.candidateId, row?.original?.firstName, row?.original?.lastName, row?.original?.referenceCode)}
                state={{ candidateFName: row?.original, candidateLName: row?.original, candidateId: row?.original?.candidateId, referenceCode: row?.original?.referenceCode }}>
                {value}</Link>)
        },
        {
            id: 'CandidateSearchResults.Column.Email',
            Header: fetchSearchResultLabel('CandidateSearchResults.Column.Email'),
            accessor: "emailAddress",
        },
        {
            id: 'CandidateSearchResults.Column.RegistrationCode',
            Header: fetchSearchResultLabel('CandidateSearchResults.Column.RegistrationCode'),
            accessor: "referenceCode",
        },
        {
            id: 'CandidateSearchResults.Column.Company',
            Header: fetchSearchResultLabel('CandidateSearchResults.Column.Company'),
            accessor: "companyName",
        },
        {
            id: 'CandidateSearchResults.Column.ProgramType',
            Header: fetchSearchResultLabel('CandidateSearchResults.Column.ProgramType'),
            accessor: "programTemplate",
        },
        {
            id: 'CandidateSearchResults.Column.ServiceOffice',
            Header: fetchSearchResultLabel('CandidateSearchResults.Column.ServiceOffice'),
            accessor: "serviceOffice",
        },
        {
            id: 'CandidateSearchResults.Column.ProgramStatus',
            Header: fetchSearchResultLabel('CandidateSearchResults.Column.ProgramStatus'),
            accessor: "status",
        },
        {
            id: 'CandidateSearchResults.Column.GenericAccount',
            Header: fetchSearchResultLabel('CandidateSearchResults.Column.GenericAccount'),
            accessor: (values) => {
                const { isGeneric } = values;
                return isGeneric ? "Yes" : "No";
            }
        },
    ];

    const tableJson = {
        searchResultTitle: candidateCMSData?.pageContent["CandidateSearchResults"]?.Title || '',
        noOfResultsDesc: fetchSearchResultLabel('CandidateSearchResults.NumberOfResults'),
        noResultsFoundDesc: fetchSearchResultLabel('CandidateSearchResults.NoResults')
    }

    const showErrorModal = () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        refErrorMessageAlert.showAlert = false;
        setErrorAlert(refErrorMessageAlert)
    }

    useEffect(() => {
        setCandidateSearchCMSData(candidateCMSData?.pageContent["CandidateSearchResults"]);
    }, [candidateCMSData]);

    return (
        <React.Fragment>
            {errorAlert?.showAlert &&
                <ErrorAlertMessage alertMessage="AllPages.ErrorAlertMessage" showAlert={showErrorModal} />
            }
            <Container fluid className="p-0 m-3">
                {searchData && searchData.length > 0 ? <SearchResultsPagination columns={searchTableColumns} data={searchData} tableJson={tableJson} candidateCMSData={candidateCMSData} />
                    : <p>{tableJson.noResultsFoundDesc}</p>}
            </Container>
        </React.Fragment>
    );
}

export default CandidateSearchResults;
